import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  PlusIcon, 
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import mixpanelService from '../services/mixpanelService';
import apiClient from '../utils/apiClient';

const StudioView = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [addingStudent, setAddingStudent] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [goalDaysPerWeek, setGoalDaysPerWeek] = useState(3);
  const [goalExercisesPerDay, setGoalExercisesPerDay] = useState(3);
  const [settingGoal, setSettingGoal] = useState(false);
  const [activityStudent, setActivityStudent] = useState(null);
  const [studentActivity, setStudentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [removingStudentId, setRemovingStudentId] = useState('');

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/students`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStudents(data);
        mixpanelService.track('Studio Roster Loaded', {
          student_count: data.length
        });
      } else {
        console.error('Failed to load students');
      }
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    if (!newStudentEmail || !newStudentEmail.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setAddingStudent(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: newStudentEmail })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message });
        setNewStudentEmail('');
        setShowAddStudent(false);
        // Reload students list
        loadStudents();
        mixpanelService.track('Studio Invitation Queued');
      } else {
        setMessage({ type: 'error', text: data.detail || data.message || 'Failed to send invitation' });
        mixpanelService.track('Studio Invitation Failed', {
          status_code: response.status,
          failure_mode: data.detail || data.message || 'Failed to send invitation'
        });
      }
    } catch (error) {
      console.error('Error inviting student:', error);
      setMessage({ type: 'error', text: 'An error occurred while sending the invitation' });
      mixpanelService.trackFailure('studio_invitation', error);
    } finally {
      setAddingStudent(false);
    }
  };

  const handleResendInvitation = async (studentId) => {
    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/resend-invitation/${studentId}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message || 'Invitation email re-queued' });
        loadStudents();
        mixpanelService.track('Studio Invitation Re-queued');
      } else {
        setMessage({ type: 'error', text: data.detail || data.message || 'Failed to resend invitation' });
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setMessage({ type: 'error', text: 'An error occurred' });
      mixpanelService.trackFailure('studio_invitation_resend', error);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/invitations/${invitationId}/revoke`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message || 'Invitation revoked' });
        loadStudents();
      } else {
        setMessage({ type: 'error', text: data.detail || data.message || 'Failed to revoke invitation' });
      }
    } catch (error) {
      console.error('Error revoking invitation:', error);
      setMessage({ type: 'error', text: 'An error occurred while revoking the invitation' });
    }
  };

  const handleViewActivity = async (student) => {
    setActivityStudent(student);
    setStudentActivity([]);
    setLoadingActivity(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/students/${student.id}/activity`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (response.ok) {
        setStudentActivity(data.attempts || []);
        mixpanelService.track('Studio Student Activity Viewed', {
          attempt_count: (data.attempts || []).length
        });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to load student activity' });
        setActivityStudent(null);
      }
    } catch (error) {
      console.error('Error loading student activity:', error);
      setMessage({ type: 'error', text: 'An error occurred while loading student activity' });
      setActivityStudent(null);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleRemoveStudent = async (student) => {
    const confirmed = window.confirm(
      `Remove ${student.first_name || 'this student'} from the studio? ` +
      'Your access to their practice history will end immediately. Their account and history will be preserved.'
    );
    if (!confirmed) return;

    setRemovingStudentId(student.id);
    setMessage({ type: '', text: '' });
    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/students/${student.id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message });
        if (activityStudent?.id === student.id) {
          setActivityStudent(null);
          setStudentActivity([]);
        }
        await loadStudents();
        mixpanelService.track('Studio Student Removed');
      } else {
        setMessage({ type: 'error', text: data.detail || data.message || 'Failed to remove student' });
      }
    } catch (error) {
      console.error('Error removing student:', error);
      setMessage({ type: 'error', text: 'An error occurred while removing the student' });
    } finally {
      setRemovingStudentId('');
    }
  };

  const formatPracticeTime = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'invited':
        return <ClockIcon className="h-5 w-5 text-yellow-600" />;
      case 'declined':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepted':
        return 'Active';
      case 'invited':
        return 'Invited';
      case 'declined':
        return 'Declined';
      default:
        return status;
    }
  };

  const handleSetGoal = (student) => {
    setSelectedStudent(student);
    setShowGoalModal(true);
  };

  const handleSaveGoal = async () => {
    if (!selectedStudent) return;

    setSettingGoal(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await apiClient.fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/student/${selectedStudent.id}/practice-goal?days_per_week=${goalDaysPerWeek}&exercises_per_day=${goalExercisesPerDay}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ 
          type: 'success', 
          text: `Practice goal set for ${selectedStudent.first_name}: ${goalDaysPerWeek} days/week, ${goalExercisesPerDay} exercises/day` 
        });
        setShowGoalModal(false);
        setSelectedStudent(null);
        mixpanelService.track('Student Practice Goal Set', {
          days_per_week: goalDaysPerWeek,
          exercises_per_day: goalExercisesPerDay
        });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to set practice goal' });
      }
    } catch (error) {
      console.error('Error setting practice goal:', error);
      setMessage({ type: 'error', text: 'An error occurred while setting the goal' });
      mixpanelService.trackFailure('student_practice_goal', error);
    } finally {
      setSettingGoal(false);
    }
  };

  const handleCloseGoalModal = () => {
    setShowGoalModal(false);
    setSelectedStudent(null);
    setGoalDaysPerWeek(3);
    setGoalExercisesPerDay(3);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center space-x-2">
            <UserGroupIcon className="h-7 w-7" />
            <span>Studio Management</span>
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          {message.text && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Loading students...
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Students in Your Studio</h2>
              </div>
              
              {students.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <UserGroupIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg text-gray-600 mb-2">No students in your studio yet</p>
                  <p className="text-sm text-gray-500">Click the button below to invite your first student</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Student
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Practice
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Practice (30d)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {students.map((student) => (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {student.first_name} {student.last_name}
                              </div>
                              <div className="text-sm text-gray-500">{student.email}</div>
                              {student.record_type === 'invitation' && student.delivery_status !== 'provider_accepted' && (
                                <div className="text-xs text-amber-700 mt-1">
                                  {student.delivery_status === 'failed'
                                    ? 'Email delivery failed — resend after checking email configuration'
                                    : 'Email has not been accepted by the provider'}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-1">
                              {getStatusIcon(student.status)}
                              <span className="text-sm text-gray-700">
                                {getStatusText(student.status)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(student.last_session)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <div>{formatPracticeTime(student.total_practice_time)}</div>
                            {student.status === 'accepted' && (
                              <div className="mt-1 text-xs text-gray-500">
                                {student.attempts_30d || 0} saved attempts
                              </div>
                            )}
                            {student.status === 'accepted' && student.recent_exercises?.length > 0 && (
                              <div className="mt-1 max-w-xs truncate text-xs text-gray-500">
                                {student.recent_exercises.join(', ')}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                            {student.status === 'invited' && (
                              <>
                                <button
                                  onClick={() => handleResendInvitation(student.id)}
                                  className="text-indigo-600 hover:text-indigo-900 font-medium"
                                >
                                  Resend Invitation
                                </button>
                                <button
                                  onClick={() => handleRevokeInvitation(student.id)}
                                  className="text-red-600 hover:text-red-900 font-medium"
                                >
                                  Revoke
                                </button>
                              </>
                            )}
                            {student.status === 'accepted' && (
                              <>
                                <button
                                  onClick={() => handleViewActivity(student)}
                                  className="text-indigo-600 hover:text-indigo-900 font-medium"
                                >
                                  View Practice
                                </button>
                                <button
                                  onClick={() => handleSetGoal(student)}
                                  className="inline-flex items-center space-x-1 text-purple-600 hover:text-purple-900 font-medium"
                                >
                                  <Cog6ToothIcon className="h-4 w-4" />
                                  <span>Set Goals</span>
                                </button>
                                <button
                                  onClick={() => handleRemoveStudent(student)}
                                  disabled={removingStudentId === student.id}
                                  className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50"
                                >
                                  {removingStudentId === student.id ? 'Removing...' : 'Remove'}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add Student Section */}
              <div className="px-6 py-4 border-t border-gray-200">
                {showAddStudent ? (
                  <div className="flex items-center space-x-3">
                    <input
                      type="email"
                      value={newStudentEmail}
                      onChange={(e) => setNewStudentEmail(e.target.value)}
                      placeholder="Enter student's email address"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={addingStudent}
                    />
                    <button
                      onClick={handleAddStudent}
                      disabled={addingStudent}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      <span>{addingStudent ? 'Sending...' : 'Send Invitation'}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowAddStudent(false);
                        setNewStudentEmail('');
                        setMessage({ type: '', text: '' });
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddStudent(true)}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center space-x-2 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5" />
                    <span>Add Student</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Student activity drill-down */}
      {activityStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {activityStudent.first_name} {activityStudent.last_name}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Recent saved practice visible through this active studio membership.
                </p>
              </div>
              <button
                onClick={() => {
                  setActivityStudent(null);
                  setStudentActivity([]);
                }}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Close
              </button>
            </div>
            <div className="overflow-auto px-6 py-4">
              {loadingActivity ? (
                <p className="py-8 text-center text-gray-500">Loading practice activity...</p>
              ) : studentActivity.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="font-medium text-gray-700">No saved practice attempts yet</p>
                  <p className="mt-1 text-sm text-gray-500">
                    Activity will appear here after the student saves an attempt.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {studentActivity.map((attempt) => (
                    <li key={attempt.id} className="py-3 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {attempt.exercise_name || 'Supported exercise'}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {attempt.exercise_type || 'Exercise'} · {formatDate(attempt.created_at)}
                        </p>
                      </div>
                      <span className="text-sm text-gray-600">
                        {formatPracticeTime(attempt.practice_minutes)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Goal Setting Modal */}
      {showGoalModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Set Practice Goals for {selectedStudent.first_name}
              </h3>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Days per week */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days per Week
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="1"
                    max="7"
                    value={goalDaysPerWeek}
                    onChange={(e) => setGoalDaysPerWeek(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold text-indigo-600 w-12 text-center">
                    {goalDaysPerWeek}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Student should practice {goalDaysPerWeek} {goalDaysPerWeek === 1 ? 'day' : 'days'} per week
                </p>
              </div>

              {/* Exercises per day */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exercises per Day
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={goalExercisesPerDay}
                    onChange={(e) => setGoalExercisesPerDay(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold text-purple-600 w-12 text-center">
                    {goalExercisesPerDay}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Each practice day should include {goalExercisesPerDay} {goalExercisesPerDay === 1 ? 'exercise' : 'exercises'}
                </p>
              </div>

              {/* Preview */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <p className="text-sm text-indigo-900 font-medium">
                  Goal Summary:
                </p>
                <p className="text-sm text-indigo-700 mt-1">
                  Practice {goalDaysPerWeek} {goalDaysPerWeek === 1 ? 'day' : 'days'} per week with at least {goalExercisesPerDay} {goalExercisesPerDay === 1 ? 'exercise' : 'exercises'} each day.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCloseGoalModal}
                disabled={settingGoal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGoal}
                disabled={settingGoal}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {settingGoal ? 'Saving...' : 'Save Goal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudioView;
